import React from "react";
import { Card, Col, InputNumber, Row, Select, Space, Tag, Typography } from "antd";
import { CheckCircleOutlined, ExclamationCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { WRITABLE_CURRENCY_CODES, getStandardCostTypeLabel } from "../../../constants";

const { Text } = Typography;
const { Option } = Select;

const STATUS_CONFIG = {
    MATCHED: {
        label: "已匹配",
        color: "green",
        icon: <CheckCircleOutlined />,
    },
    MULTIPLE_USABLE_MATCHES: {
        label: "多合同",
        color: "orange",
        icon: <ExclamationCircleOutlined />,
    },
    NO_USABLE_CONTRACT: {
        label: "无合同",
        color: "red",
        icon: <ExclamationCircleOutlined />,
    },
    DIMENSION_INCOMPLETE: {
        label: "维度缺失",
        color: "gold",
        icon: <InfoCircleOutlined />,
    },
    MATCH_ERROR: {
        label: "查询失败",
        color: "red",
        icon: <ExclamationCircleOutlined />,
    },
};

const formatDate = (value) => {
    if (!value) return "-";
    return String(value).slice(0, 10);
};

const toFiniteNumber = (value) => {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
};

const normalizeCurrency = (value) => {
    if (!value) return null;
    const normalized = String(value).trim().toUpperCase();
    return normalized || null;
};

const resolveSectionTransactionTotal = (section) => {
    const expectedSubTotal = toFiniteNumber(section?.expected_sub_total);
    if (expectedSubTotal !== null) return expectedSubTotal;

    const subTotal = toFiniteNumber(section?.sub_total);
    if (subTotal !== null) return subTotal;

    return (section?.line_items || []).reduce((total, item) => total + (toFiniteNumber(item?.total_price) ?? 0), 0);
};

const resolveInvoiceTransactionTotal = (invoice) => {
    const sections = Array.isArray(invoice?.sections) ? invoice.sections : [];
    if (sections.length > 0) {
        return sections.reduce((total, section) => total + resolveSectionTransactionTotal(section), 0);
    }

    return toFiniteNumber(invoice?.meta?.expected_grand_total) ?? toFiniteNumber(invoice?.meta?.grand_total);
};

const formatRateHint = (settlementTotal, transactionTotal) => {
    const settlementAmount = toFiniteNumber(settlementTotal);
    const transactionAmount = toFiniteNumber(transactionTotal);
    if (settlementAmount === null || transactionAmount === null || transactionAmount === 0) return null;

    return (settlementAmount / transactionAmount).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 6,
    });
};

const ContractMatchPanel = ({ contractMatchResult, updateReconciliationData, invoices, threeTierReadiness }) => {
    if (!contractMatchResult) return null;

    const { status, message } = contractMatchResult;
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.DIMENSION_INCOMPLETE;
    const dimensions = contractMatchResult.match_dimensions || {};
    const contracts = Array.isArray(contractMatchResult.contracts) ? contractMatchResult.contracts : [];
    const suggestedCurrency = contractMatchResult.suggested_settlement_currency;
    const readinessInvoices = Array.isArray(threeTierReadiness?.invoices) ? threeTierReadiness.invoices : [];
    const readinessByInvoiceIndex = new Map(readinessInvoices.map((item) => [item.invoice_index, item]));

    // 结算币种：优先取已写入 payload 的值，其次取合同建议值。
    // 合同匹配是 IDS 级别（四元组），结果对同一批次所有账单通用，故写入全部 invoices。
    const currentSettlementCurrency = invoices?.[0]?.meta?.settlement_currency || suggestedCurrency;
    const canPickSettlementCurrency = status === "NO_USABLE_CONTRACT" && typeof updateReconciliationData === "function";

    const handleSettlementCurrencyChange = (value) => {
        if (typeof updateReconciliationData !== "function") return;
        const count = Array.isArray(invoices) ? invoices.length : 0;
        for (let i = 0; i < count; i++) {
            updateReconciliationData(`invoices[${i}].meta.settlement_currency`, value);
        }
    };

    const handleSettlementAmountChange = (invoiceIndex, value) => {
        if (typeof updateReconciliationData !== "function") return;
        updateReconciliationData(`invoices[${invoiceIndex}].meta.settlement_total_amount`, value);
    };

    const invoiceList = Array.isArray(invoices) ? invoices : [];
    const settlementAmountRows = invoiceList
        .map((invoice, invoiceIndex) => {
            const invoiceMeta = invoice?.meta || {};
            const invoiceReadiness = readinessByInvoiceIndex.get(invoiceIndex) || {};
            const hasInvoiceReadiness = readinessByInvoiceIndex.has(invoiceIndex);
            const settlementCurrency = invoiceMeta.settlement_currency || invoiceReadiness.settlement_currency || currentSettlementCurrency;
            const settlementTotal = invoiceMeta.settlement_total_amount;
            const hasSettlementTotal = settlementTotal !== undefined && settlementTotal !== null && settlementTotal !== "";
            const needsManualInput = hasInvoiceReadiness ? Boolean(invoiceReadiness.needs_manual_input) : Boolean(threeTierReadiness?.needs_manual_input);
            const alphaSource = invoiceReadiness.alpha_source || threeTierReadiness?.alpha_source;
            const transactionCurrency = normalizeCurrency(invoiceReadiness.transaction_currency || threeTierReadiness?.transaction_currency);
            const hasLocalSettlementCurrency = Boolean(normalizeCurrency(invoiceMeta.settlement_currency));
            const localSettlementMatchesTransaction = Boolean(
                hasLocalSettlementCurrency
                    && transactionCurrency
                    && normalizeCurrency(settlementCurrency) === transactionCurrency,
            );
            const needsLocalManualAmount = Boolean(
                hasLocalSettlementCurrency
                    && transactionCurrency
                    && normalizeCurrency(settlementCurrency) !== transactionCurrency,
            );
            const shouldShow = Boolean(transactionCurrency) && alphaSource !== "NOT_APPLICABLE" && !localSettlementMatchesTransaction && (needsManualInput || hasSettlementTotal || needsLocalManualAmount);

            if (!shouldShow) return null;

            const transactionTotal = resolveInvoiceTransactionTotal(invoice);
            const rateHint = formatRateHint(settlementTotal, transactionTotal);
            const invoiceLabel = invoiceMeta.invoice_number && invoiceMeta.invoice_number !== "-" ? invoiceMeta.invoice_number : `第${invoiceIndex + 1}张`;
            const readOnly = Boolean(threeTierReadiness?.is_ready && hasSettlementTotal && !needsManualInput);

            return (
                <Space key={invoiceIndex} wrap size={6} style={{ paddingTop: 6 }}>
                    <Text type="secondary">{invoiceList.length > 1 ? `${invoiceLabel} 结算金额` : "结算金额"}</Text>
                    <InputNumber
                        size="small"
                        precision={2}
                        value={settlementTotal}
                        onChange={(value) => handleSettlementAmountChange(invoiceIndex, value)}
                        addonAfter={settlementCurrency || "币种"}
                        placeholder="金额"
                        readOnly={readOnly}
                        controls={!readOnly}
                        style={{ width: 190 }}
                    />
                    {rateHint && <Text type="secondary">汇率 ≈ {rateHint}</Text>}
                </Space>
            );
        })
        .filter(Boolean);

    return (
        <Card size="small" style={{ marginTop: 12, background: "#fcfffe", borderColor: "#d9f7be" }} bodyStyle={{ padding: 12 }}>
            <Row gutter={[12, 8]} align="middle">
                <Col flex="auto">
                    <Space wrap size="small">
                        <Text strong>合同</Text>
                        <Tag color={config.color} icon={config.icon} style={{ margin: 0 }}>
                            {config.label}
                        </Tag>
                        {suggestedCurrency && (
                            <Tag color="cyan" style={{ margin: 0 }}>
                                结算: {suggestedCurrency}
                            </Tag>
                        )}
                        {canPickSettlementCurrency && (
                            <Space size={4}>
                                <Text type="secondary">手选:</Text>
                                <Select
                                    size="small"
                                    value={currentSettlementCurrency}
                                    onChange={handleSettlementCurrencyChange}
                                    placeholder="结算币种"
                                    style={{ width: 110 }}
                                >
                                    {WRITABLE_CURRENCY_CODES.map((c) => (
                                        <Option key={c} value={c}>
                                            {c}
                                        </Option>
                                    ))}
                                </Select>
                            </Space>
                        )}
                        {message && <Text type="secondary">{message}</Text>}
                    </Space>
                </Col>
                <Col>
                    <Space wrap size={[4, 4]}>
                        {dimensions.cost_type && <Tag>{getStandardCostTypeLabel(dimensions.cost_type)}</Tag>}
                        {dimensions.airport_code && <Tag>机场 {dimensions.airport_code}</Tag>}
                        {dimensions.invoice_date && <Tag>账单日 {formatDate(dimensions.invoice_date)}</Tag>}
                    </Space>
                </Col>
            </Row>

            {settlementAmountRows.length > 0 && (
                <Space direction="vertical" size={4} style={{ width: "100%", marginTop: 8 }}>
                    {settlementAmountRows}
                </Space>
            )}

            {contracts.length > 0 && (
                <Space direction="vertical" size={6} style={{ width: "100%", marginTop: 10 }}>
                    {contracts.map((contract) => (
                        <div key={contract.contract_id} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <Text strong>{contract.contract_name || `合同 #${contract.contract_id}`}</Text>
                            {contract.contract_number && <Text type="secondary">{contract.contract_number}</Text>}
                            <Tag color={contract.is_usable_match ? "green" : "default"} style={{ margin: 0 }}>
                                {contract.is_usable_match ? "可用" : "不可用"}
                            </Tag>
                            <Tag color="purple" style={{ margin: 0 }}>
                                {contract.settlement_currency || "未配置"}
                            </Tag>
                            <Text type="secondary">
                                有效期 {formatDate(contract.valid_from)} 至 {formatDate(contract.valid_to)}
                            </Text>
                        </div>
                    ))}
                </Space>
            )}
        </Card>
    );
};

export default ContractMatchPanel;
